import { useEffect, useRef, useState } from "react";
import arrowBack from "../../Asserts/images/arrow_back.svg";
import Filter from "../../Asserts/images/Filter.svg";
import DownloadIcon from "../../Asserts/images/download-white.svg";
import axios from "axios";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { baseURL } from "../../services/axiosConfig";
import { refreshTokenV2 } from "../../services/api";
import { useDispatch } from "react-redux";

const CircularLoader = () => (
  <div className="flex flex-col items-center justify-center py-8">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
    <div className="text-blue-500 font-medium">Data fetching...</div>
  </div>
);

const FilterDropDown = [
  { id: 1, title: "all", lable: "All" },
  { id: 2, title: "active", lable: "Active" },
  { id: 3, title: "in_active", lable: "Inactive" },
];

function OtherPlayesPredictions({
  otherPlayersPrediction,
  allOtherPlayerData, // <-- NEW PROP: full unfiltered data
  seasonId,
  currentMatchWeek,
  potId,
  pickId,
  isOtherPlayerLoading,
  setOtherPlayerFilter,
  btnName
}) {
  const dispatch = useDispatch()
  const [visibleMWCount, setVisibleMWCount] = useState(getVisibleCount());
  const [startIndex, setStartIndex] = useState(0);

  function getVisibleCount() {
    if (window.innerWidth >= 1024) return 4;
    if (window.innerWidth >= 768) return 3;
    return 3;
  }

  useEffect(() => {
    const handleResize = () => {
      setVisibleMWCount(getVisibleCount());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const totalCols = Math.max(
    0,
    (otherPlayersPrediction?.end_matchweek ?? 0) -
    (otherPlayersPrediction?.start_matchweek ?? 0) +
    1
  );
  useEffect(() => {
    const maxStart = Math.max(0, totalCols - visibleMWCount);
    setStartIndex((prev) => Math.min(prev, maxStart));
  }, [visibleMWCount, totalCols]);

  const atStart = startIndex === 0;
  const atEnd = startIndex + visibleMWCount >= totalCols;

  const handlePrev = () => {
    setStartIndex((prev) => Math.max(0, prev - visibleMWCount));
  };

  const handleNext = () => {
    const maxStart = Math.max(0, totalCols - visibleMWCount);
    setStartIndex((prev) => Math.min(maxStart, prev + visibleMWCount));
  };

  const headerVal = Array.from(
    {
      length:
        otherPlayersPrediction?.end_matchweek -
        otherPlayersPrediction?.start_matchweek +
        1,
    },
    (_, i) => otherPlayersPrediction?.start_matchweek + i
  ).reverse();
  const visibleHeaders = headerVal.slice(
    startIndex,
    Math.min(startIndex + visibleMWCount, headerVal.length)
  );

  const otherPlayersPredictionExcelDownload = async () => {
    const token = localStorage.getItem("access_token");

    try {
      const response = await axios.get(
        `${baseURL}/pot/otherplayerpredictions/${potId}/download`,
        {
          headers: {
            Authorization: `Bearer ${JSON.parse(token)}`,
          },
          responseType: "blob",
        }
      );

      const text = await response.data.text();
      const rows = text
        .trim()
        .split("\n")
        .map((row) => row.split(","));

      const headers = rows[0];
      const dataRows = rows.slice(1);

      const finalData = dataRows.map((row) => {
        const rowData = {};
        headers.forEach((header, index) => {
          rowData[header] = row[index];
        });
        return rowData;
      });

      const worksheet = XLSX.utils.json_to_sheet(finalData);

      headers.forEach((_, colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex });
        if (worksheet[cellRef]) {
          worksheet[cellRef].s = {
            font: { bold: true },
          };
        }
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Predictions");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, `PlayerPredictions-${potId}.xlsx`);
    } catch (error) {
      if (error?.response?.status === 401) {
        refreshTokenV2(dispatch);
      }
      console.error("Download failed:", error);
    }
  };

  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 👇 Ensure pickId row is in the list even after filtering
  let filteredData = [...(otherPlayersPrediction?.data || [])];
  const selectedExists = filteredData.some((p) => p.pick_id === pickId);
  if (!selectedExists && pickId) {
    const selectedFromFull = allOtherPlayerData?.find((p) => p.pick_id === pickId);
    if (selectedFromFull) {
      filteredData = [selectedFromFull, ...filteredData];
    }
  }

  return (
    <div className="w-full lg:w-1/3 xl:h-[700px] md:h-[600px] flex flex-col bg-white rounded-[12px] border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b-4 border-black text-center rounded-tl-[12px] rounded-tr-[12px] flex-shrink-0 flex items-center justify-between">
        <div className={`${btnName === 'viewDetails' ? 'items-center justify-center text-center w-full' : 'text-left'}`}>
          <h2 className="text-sm md:text-lg font-semibold font-archivo">
            Other Player Picks
          </h2>
          <div className="text-[12px] md:text-sm text-gray-600 flex justify-center items-center gap-2 mt-1">
            <span className="font-archivo font-bold">Default Picks</span>
            <div className="flex gap-2 md:gap-4">
              <div className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  className="w-4 h-4 appearance-none border-2 border-green-500 rounded-sm"
                  id="won"
                />
                <label htmlFor="won" className="font-archivo text-sm md:text-base leading-none">
                  Won
                </label>
              </div>
              <div className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  className="w-4 h-4 appearance-none border-2 border-red-500 rounded-sm"
                  id="loss"
                />
                <label htmlFor="loss" className="font-archivo text-sm md:text-base leading-none">
                  Loss
                </label>
              </div>
            </div>
          </div>
        </div>
        {
          btnName !== 'viewDetails' && !headerVal.includes(0) && <button
            className="flex items-center justify-between text-[10px] md:text-sm font-semibold gap-2 px-2 py-1.5 bg-[#007FFF] text-white rounded-lg font-archivo"
            onClick={otherPlayersPredictionExcelDownload}
          >
            Download
            <img src={DownloadIcon} alt={"download-icon"} />
          </button>
        }

      </div>

      {/* Table */}
      {isOtherPlayerLoading ? (
        <CircularLoader />
      ) : (
        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-x-auto">
            <table className="min-w-full text-sm text-left table-fixed mb-4">
              <thead className="bg-[#00C05D] text-white sticky top-0 z-10">
                <tr className="items-center">
                  <th className="p-4 flex gap-2 text-[16px] font-semibold text-sm w-32">
                    Players{" "}
                    <img
                      src={Filter}
                      alt="filter"
                      className="w-6 h-6 cursor-pointer"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setShowDropdown((prev) => !prev);
                      }}
                    />
                  </th>
                  {showDropdown && (
                    <div
                      ref={dropdownRef}
                      className="absolute left-0 mt-2 z-10 bg-white border border-gray-300 shadow-lg rounded-md w-28"
                    >
                      <ul className="text-sm text-gray-700">
                        {FilterDropDown.map((ele) => (
                          <li
                            key={ele.id}
                            className="px-4 py-2 hover:bg-blue-100 hover:text-blue-600 cursor-pointer"
                            onClick={() => {
                              setOtherPlayerFilter(
                                ele.title === "all"
                                  ? null
                                  : ele.title === "active"
                                    ? true
                                    : false
                              );
                              setShowDropdown(false);
                            }}
                          >
                            {ele.lable}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <th
                    className={`w-8 md:w-12 text-center cursor-pointer ${atStart ? "opacity-50 pointer-events-none" : ""}`}
                    onClick={handlePrev}
                    style={{ minWidth: '50px' }}
                  >
                    <img
                      src={arrowBack}
                      className="h-6 w-6 md:h-7 md:w-7 mx-auto cursor-pointer"
                      alt="Prev"
                    />
                  </th>

                  {visibleHeaders.map((ele) => (
                    <th
                      key={`header-MW-${ele}`}
                      className="px-4 py-2 text-[16px] text-xs font-semibold text-center w-20"
                    >
                      MW-{ele}
                    </th>
                  ))}

                  <th
                    className={`w-8 md:w-12 text-center cursor-pointer ${atEnd ? "opacity-50 pointer-events-none" : ""}`}
                    onClick={handleNext}
                    style={{ minWidth: '48px' }}
                  >
                    <img
                      src={arrowBack}
                      className="h-6 w-6 md:h-7 md:w-7 mx-auto"
                      style={{
                        transform: 'rotate(180deg)'
                      }}
                      alt="Next"
                    />
                  </th>
                </tr>
              </thead>

              <tbody className="overflow-y-auto">
                {filteredData.length > 0 ? (
                  filteredData.map((player, idx) => (
                    <tr
                      key={idx}
                      className={`mb-8 ${pickId === player.pick_id
                        ? "bg-blue-100 rounded-lg border-[1px] border-solid  border-blue-500 m-2"
                        : ""
                        }`}
                    >
                      <td className="px-4 py-2 font-medium text-gray-800 w-32">
                        {player.player}
                      </td>

                      <td className="w-8"></td>

                      {visibleHeaders.map((ele) => {
                        const key = `MW-${ele}`;
                        const matchWeekData = player[key];
                        const pickResult = matchWeekData?.pick_result;
                        const isSystemPrediction = matchWeekData?.system;
                        return (
                          <td
                            key={key}
                            className="px-4 py-2 text-center w-20"
                          >
                            {matchWeekData?.logo ? (
                              <div
                                className={`p-1 inline-block ${isSystemPrediction && pickResult === "Win"
                                  ? "border-2 border-green-500 rounded-[6px]"
                                  : isSystemPrediction &&
                                    pickResult === "Lost"
                                    ? "border-2 border-red-500 rounded-[6px]"
                                    : ""
                                  }`}
                              >
                                <img
                                  src={matchWeekData.logo}
                                  alt="logo"
                                  className="h-5 w-5 md:h-12 md:w-12 mx-auto"
                                />
                              </div>
                            ) : (
                              <p className="lg:text-xl text-lg text-red-500">
                                X
                              </p>
                            )}
                          </td>
                        );
                      })}

                      <td className="w-8"></td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={visibleHeaders.length + 3} className="text-center py-8">
                      <p className="text-gray-500">
                        Other player picks will appear after the entry deadline
                        closes!
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default OtherPlayesPredictions;
